// Small celebration helpers using Reelty's brand palette.
// - fireBurst(el):       small confetti burst emanating from a specific element
// - fireMilestone():     a bigger, spreadier burst for major moments (100% complete / approval)
// - fireGrandFinale():   the once-in-a-lifetime "Reelty Certified" celebration —
//                        4-corner cannons + gold coin rain + sustained trickle over ~4.5s
// - fireBeatBurst():     punchy centered burst used on each word-slam "beat" in the finale
// - fireHoloExplosion(): gold-only fan burst used when the holographic ID card lands
import confetti from "canvas-confetti";

// Reelty palette: Oxblood · Cream · Gold · Dark Gold · Cream tint
const REELTY_COLORS = ["#6B0F1A", "#8A1424", "#B08D57", "#B08D57", "#F5EBD9"];

function elementOrigin(el) {
  if (!el || !el.getBoundingClientRect) return { x: 0.5, y: 0.5 };
  const r = el.getBoundingClientRect();
  return {
    x: (r.left + r.width / 2) / window.innerWidth,
    y: (r.top + r.height / 2) / window.innerHeight,
  };
}

export function fireBurst(el) {
  if (typeof window === "undefined") return;
  const origin = elementOrigin(el);
  confetti({
    particleCount: 32,
    spread: 55,
    startVelocity: 28,
    gravity: 0.9,
    ticks: 120,
    scalar: 0.75,
    origin,
    colors: REELTY_COLORS,
    disableForReducedMotion: true,
  });
}

export function fireMilestone() {
  if (typeof window === "undefined") return;
  const duration = 900;
  const end = Date.now() + duration;
  const shoot = (x) =>
    confetti({
      particleCount: 60,
      spread: 78,
      startVelocity: 42,
      gravity: 0.85,
      ticks: 200,
      scalar: 1,
      origin: { x, y: 0.72 },
      colors: REELTY_COLORS,
      disableForReducedMotion: true,
    });
  shoot(0.22);
  shoot(0.78);
  (function frame() {
    shoot(Math.random());
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
}

// The once-per-user "Reelty Certified" moment — deliberately over-the-top:
// 4 corner cannons fire angled inward, sustained gold-coin rain from the top,
// and a final oxblood + gold sparkle sweep. Total ≈ 4.5s.
const GOLD_TONES = ["#B08D57", "#D4B584", "#B08D57", "#F5EBD9"];
const OXBLOOD_TONES = ["#6B0F1A", "#8A1424", "#3E060F"];

export function fireGrandFinale() {
  if (typeof window === "undefined") return;
  // Corner cannons — fire in pairs, angled toward the center.
  const cornerShot = (x, y, angle) =>
    confetti({
      particleCount: 90,
      spread: 65,
      startVelocity: 70,
      angle,
      gravity: 0.9,
      ticks: 260,
      scalar: 1.15,
      origin: { x, y },
      colors: [...REELTY_COLORS, ...GOLD_TONES],
      shapes: ["circle", "square"],
      disableForReducedMotion: true,
    });
  cornerShot(0, 1, 55);      // bottom-left → up-right
  cornerShot(1, 1, 125);     // bottom-right → up-left
  setTimeout(() => {
    cornerShot(0, 0, -55);   // top-left → down-right
    cornerShot(1, 0, -125);  // top-right → down-left
  }, 180);

  // Sustained gold coin rain from the top for ~3.2s.
  const rainEnd = Date.now() + 3200;
  (function rain() {
    confetti({
      particleCount: 6,
      startVelocity: 12,
      spread: 360,
      ticks: 320,
      gravity: 0.55,
      scalar: 1.05,
      origin: { x: Math.random(), y: -0.05 },
      colors: GOLD_TONES,
      shapes: ["circle"],
      disableForReducedMotion: true,
    });
    if (Date.now() < rainEnd) setTimeout(rain, 90);
  })();

  // Oxblood + gold final sparkle sweep at ~1.4s in.
  setTimeout(() => {
    for (let i = 0; i < 5; i++) {
      confetti({
        particleCount: 40,
        spread: 110,
        startVelocity: 38,
        gravity: 0.75,
        ticks: 220,
        scalar: 0.9,
        origin: { x: 0.15 + i * 0.18, y: 0.6 },
        colors: [...OXBLOOD_TONES, ...GOLD_TONES],
        disableForReducedMotion: true,
      });
    }
  }, 1400);
}

// Punchy centered burst — used per word beat in the finale for a "bass drop" feel.
export function fireBeatBurst(colorSet = "mixed") {
  if (typeof window === "undefined") return;
  const colors =
    colorSet === "gold" ? GOLD_TONES
    : colorSet === "oxblood" ? OXBLOOD_TONES
    : [...REELTY_COLORS, ...GOLD_TONES];
  // Two overlapping pops for extra punch.
  confetti({
    particleCount: 55,
    spread: 360,
    startVelocity: 45,
    ticks: 160,
    scalar: 1.1,
    origin: { x: 0.5, y: 0.5 },
    colors,
    disableForReducedMotion: true,
  });
  confetti({
    particleCount: 25,
    spread: 90,
    startVelocity: 60,
    angle: 90,
    gravity: 0.6,
    ticks: 200,
    scalar: 1.3,
    origin: { x: 0.5, y: 0.6 },
    colors,
    shapes: ["circle"],
    disableForReducedMotion: true,
  });
}

// Gold-only fan burst — fires when the holographic ID card slams into place.
export function fireHoloExplosion() {
  if (typeof window === "undefined") return;
  // Wide symmetric gold fan.
  [65, 115].forEach((angle) =>
    confetti({
      particleCount: 120,
      spread: 80,
      startVelocity: 55,
      angle,
      gravity: 0.9,
      ticks: 240,
      scalar: 1.2,
      origin: { x: 0.5, y: 0.75 },
      colors: GOLD_TONES,
      shapes: ["circle", "square"],
      disableForReducedMotion: true,
    })
  );
  // Fine-glitter dust cloud upward.
  confetti({
    particleCount: 80,
    spread: 140,
    startVelocity: 30,
    gravity: 0.4,
    ticks: 300,
    scalar: 0.6,
    origin: { x: 0.5, y: 0.7 },
    colors: ["#F5EBD9", "#D4B584", "#B08D57"],
    shapes: ["circle"],
    disableForReducedMotion: true,
  });
}
